import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { AppLayout } from '../components/Layout';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search, Award, Clock, DollarSign, MapPin, CheckCircle2, ExternalLink,
  Filter, ChevronDown, X, Star, Zap
} from 'lucide-react';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLUSTER_COLORS = {
  agr: 'emerald', arc: 'amber', art: 'purple', bus: 'blue', edu: 'sky',
  fin: 'green', gov: 'slate', hlt: 'red', hos: 'orange', hum: 'pink',
  it: 'cyan', law: 'indigo', mfg: 'yellow', mkt: 'rose', stem: 'violet', trn: 'teal',
};

const COLOR_CLASSES = {
  emerald: { tag: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', dot: 'bg-emerald-400' },
  amber: { tag: 'bg-amber-500/15 text-amber-300 border-amber-500/30', dot: 'bg-amber-400' },
  purple: { tag: 'bg-purple-500/15 text-purple-300 border-purple-500/30', dot: 'bg-purple-400' },
  blue: { tag: 'bg-blue-500/15 text-blue-300 border-blue-500/30', dot: 'bg-blue-400' },
  sky: { tag: 'bg-sky-500/15 text-sky-300 border-sky-500/30', dot: 'bg-sky-400' },
  green: { tag: 'bg-green-500/15 text-green-300 border-green-500/30', dot: 'bg-green-400' },
  slate: { tag: 'bg-slate-700/30 text-slate-300 border-slate-600/30', dot: 'bg-slate-400' },
  red: { tag: 'bg-red-500/15 text-red-300 border-red-500/30', dot: 'bg-red-400' },
  orange: { tag: 'bg-orange-500/15 text-orange-300 border-orange-500/30', dot: 'bg-orange-400' },
  pink: { tag: 'bg-pink-500/15 text-pink-300 border-pink-500/30', dot: 'bg-pink-400' },
  cyan: { tag: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30', dot: 'bg-cyan-400' },
  indigo: { tag: 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30', dot: 'bg-indigo-400' },
  yellow: { tag: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30', dot: 'bg-yellow-400' },
  rose: { tag: 'bg-rose-500/15 text-rose-300 border-rose-500/30', dot: 'bg-rose-400' },
  violet: { tag: 'bg-violet-500/15 text-violet-300 border-violet-500/30', dot: 'bg-violet-400' },
  teal: { tag: 'bg-teal-500/15 text-teal-300 border-teal-500/30', dot: 'bg-teal-400' },
};

function ProgramCard({ prog, index }) {
  const color = CLUSTER_COLORS[prog.cluster] || 'slate';
  const classes = COLOR_CLASSES[color] || COLOR_CLASSES.slate;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04 }}
      className="rounded-xl border border-slate-700/50 bg-slate-800/40 p-4 hover:border-slate-600 transition-colors"
      data-testid="cert-program-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${classes.tag}`}>
              {prog.cluster_name}
            </span>
            {prog.ic_eligible && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30 flex items-center gap-1">
                <Star className="w-2.5 h-2.5" /> NC Industry Credential
              </span>
            )}
            {prog.nc_only && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-slate-700 text-slate-300 border border-slate-600">
                NC Specific
              </span>
            )}
          </div>

          <h3 className="text-white font-semibold text-sm leading-snug">{prog.name}</h3>
          <p className="text-slate-300 text-xs mt-1 line-clamp-2">{prog.description}</p>

          <div className="flex items-center gap-4 mt-3 text-xs text-slate-300 flex-wrap">
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {prog.duration_weeks < 4
                ? `${prog.duration_weeks * 5} days`
                : prog.duration_weeks < 52
                ? `${prog.duration_weeks} weeks`
                : '1 year'}
            </span>
            <span className="flex items-center gap-1">
              <DollarSign className="w-3 h-3" />
              {prog.cost_est === 0 ? 'Free' : `~$${prog.cost_est.toLocaleString()}`}
            </span>
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {prog.provider}
            </span>
          </div>
        </div>

        {prog.fit_score > 0 && (
          <div className="flex-shrink-0 text-right">
            <div className="flex items-center gap-1 text-cyan-400 text-xs font-medium">
              <Zap className="w-3 h-3" />
              Match
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default function CertificateProgramsPage() {
  const { getAuthHeader } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [cluster, setCluster] = useState('');
  const [icOnly, setIcOnly] = useState(false);
  const [clusters, setClusters] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (cluster) params.set('cluster', cluster);
      if (icOnly) params.set('ic_only', 'true');

      const [progsRes, clustersRes] = await Promise.all([
        axios.get(`${API}/nc/certificates/search?${params}`, getAuthHeader()),
        clusters.length === 0 ? axios.get(`${API}/nc/cte-pathways`, getAuthHeader()) : null,
      ]);

      setPrograms(progsRes.data.programs || []);
      if (clustersRes) setClusters(clustersRes.data.clusters || []);
    } catch {
      toast.error('Could not load certificate programs');
    } finally {
      setLoading(false);
    }
  }, [q, cluster, icOnly, getAuthHeader]);

  useEffect(() => { load(); }, [cluster, icOnly]);

  const handleSearch = (e) => { e.preventDefault(); load(); };

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto space-y-6 pb-12" data-testid="cert-programs-page">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-semibold uppercase tracking-widest text-cyan-400">Education</span>
          </div>
          <h1 className="text-3xl font-bold text-white">Certificate & Non-Degree Programs</h1>
          <p className="text-slate-300 text-sm mt-1">
            Fast-track credentials that count toward NC CTE pathways and Career Endorsement.
            {' '}<span className="text-amber-400">★ NC Industry Credential</span> programs qualify toward your diploma endorsement.
          </p>
        </motion.div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
              <input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Search programs (e.g. nursing, IT, welding)..."
                className="w-full pl-9 pr-4 py-2.5 bg-slate-800/60 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
                data-testid="cert-search-input"
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2.5 rounded-lg bg-cyan-600 text-white text-sm font-medium hover:bg-cyan-500 transition-colors"
            >
              Search
            </button>
          </form>

          <select
            value={cluster}
            onChange={e => setCluster(e.target.value)}
            className="px-3 py-2.5 bg-slate-800/60 border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:border-cyan-500"
            data-testid="cert-cluster-filter"
          >
            <option value="">All Career Clusters</option>
            {clusters.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>

          <button
            onClick={() => setIcOnly(!icOnly)}
            className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors flex items-center gap-2 ${
              icOnly ? 'bg-amber-500/20 border-amber-500/40 text-amber-300' : 'bg-slate-800/60 border-slate-700 text-slate-300 hover:text-white'
            }`}
            data-testid="cert-ic-filter"
          >
            <Star className="w-3.5 h-3.5" /> NC IC Only
          </button>
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-6 h-6 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : programs.length === 0 ? (
          <div className="text-center py-12 text-slate-300">
            <Award className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p>No programs match your search. Try a different term or remove filters.</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-300">{programs.length} programs found, sorted by career fit</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {programs.map((prog, i) => (
                <ProgramCard key={prog.id} prog={prog} index={i} />
              ))}
            </div>
          </>
        )}

        <div className="text-xs text-slate-300 text-center pt-4">
          NC Industry Credentials are recognized by NC DPI for the Career Endorsement diploma.{' '}
          <a href="https://www.dpi.nc.gov/districts-schools/high-school-graduation-requirements/high-school-diploma-endorsements" target="_blank" rel="noopener noreferrer" className="text-cyan-500 hover:text-cyan-400">
            Learn more at DPI
          </a>
        </div>
      </div>
    </AppLayout>
  );
}
